/** Ledger action codes for History tables (options + stock). */

import { ledgerAttachedNote, renderTxnNoteBadge } from "./txnNote";
import type { HistoryRelatedTxn } from "./historyRelated";

export interface HistoryActionTxn {
  id?: number;
  instrument_type?: string;
  action?: string;
  close_reason?: string;
  option_trans_code?: string;
  extensions?: Record<string, string>;
  notes_preview?: string;
  note?: string;
  notes?: Array<{ body?: string | null; kind?: string | null }>;
  cash_flow?: boolean;
}

function stockOpenTranType(txn: HistoryActionTxn): string {
  return String(txn.extensions?.open_tran_type ?? "").trim().toLowerCase();
}

export function isStockAccountTransfer(txn: HistoryActionTxn): boolean {
  if (txn.instrument_type !== "stock") return false;
  const openType = stockOpenTranType(txn);
  if (openType === "taxlottransfer" || openType === "deposit") return true;
  return String(txn.extensions?.import_source ?? "").toLowerCase() === "tax_lot_transfer";
}

export function historyActionLabel(txn: HistoryActionTxn): string {
  const code = (txn.option_trans_code ?? "").toUpperCase();
  const action = (txn.action ?? "").toLowerCase();
  if (txn.instrument_type === "cash") {
    const cat = String(
      (txn as HistoryActionTxn & { cash_flow_category?: string }).cash_flow_category ??
        txn.action ??
        "",
    )
      .trim()
      .toLowerCase();
    const direction = String(
      (txn as HistoryActionTxn & { cash_flow_direction?: string }).cash_flow_direction ?? "",
    ).toLowerCase();
    if (cat === "deposit") return "DEP";
    if (cat === "withdrawal") return "WDL";
    if (cat === "tax") return "TAX";
    if (cat === "margin_interest") return "MARG";
    if (cat === "stock_lending_interest") return "LEND";
    if (cat === "transfer_in") return "XFER+";
    if (cat === "transfer_out") return "XFER-";
    if (cat === "adjustment") return direction === "in" ? "ADJ+" : "ADJ-";
    return direction === "in" ? "IN" : "OUT";
  }
  if (txn.instrument_type === "stock") {
    if (isStockAccountTransfer(txn)) return "XFER";
    if (action === "sell") return "SELL";
    if (action === "buy") return "BUY";
    return (txn.action ?? "—").toUpperCase();
  }

  if (code === "OASGN" || code === "ASGN") return "ASGN";
  if (code === "OEXP" || code === "EXP") return "EXP";
  if (code === "STO" || code === "BTC" || code === "BTO" || code === "STC") return code;
  if (txn.close_reason === "assigned") return "ASGN";
  if (txn.close_reason === "expired") return "EXP";
  if (action === "sell") return "STO";
  if (action === "buy") return "BTC";
  return (txn.action ?? "—").toUpperCase();
}

/** True when RH still shows expiration/assignment as pending settlement. */
export function isPendingSettlement(txn: HistoryActionTxn): boolean {
  return String(txn.extensions?.settlement_status ?? "").toLowerCase() === "pending";
}

/** UI-only label for the Notes column (never persisted to notes). */
export function historyPendingSettlementDisplay(txn: HistoryActionTxn): string {
  return isPendingSettlement(txn) ? "Pending settlement" : "";
}

/** Buy/Sell prefix for assignment rows in History detail column only. */
export function historyBuySellLabel(txn: HistoryActionTxn & { side?: string }): string {
  const code = historyActionLabel(txn);
  if (code !== "ASGN" && code !== "OASGN") return "";
  const side = (txn.side ?? "").toLowerCase();
  if (side === "put") return "Buy";
  if (side === "call") return "Sell";
  return "";
}

/** CSS classes for native `.hist-pill` styling. */
export function historyActionPillClasses(label: string): string {
  const base = "hist-pill hist-action-code";
  if (label === "STO" || label === "SELL" || label === "STC") {
    return `${base} hist-buy`;
  }
  if (label === "BTC" || label === "BUY" || label === "BTO") {
    return `${base} hist-sell`;
  }
  if (label === "EXP") {
    return `${base} hist-neutral`;
  }
  if (label === "ASGN") {
    return `${base} hist-asgn`;
  }
  if (label === "DEP" || label === "LEND" || label === "XFER+" || label === "XFER" || label === "ADJ+") {
    return `${base} hist-buy`;
  }
  if (label === "WDL" || label === "TAX" || label === "MARG" || label === "XFER-" || label === "ADJ-" || label === "OUT") {
    return `${base} hist-sell`;
  }
  return base;
}

export function renderHistoryActionPill(txn: HistoryActionTxn): string {
  const label = historyActionLabel(txn);
  if (label === "—") return `<span class="hist-pill">—</span>`;
  const related = txn as HistoryActionTxn & HistoryRelatedTxn;
  const cashFlowId =
    related.instrument_type === "cash" || related.cash_flow === true
      ? Math.abs(Number(related.id) || 0)
      : 0;
  const txnId = cashFlowId > 0 ? 0 : Number(related.id) || 0;
  return renderTxnNoteBadge({
    label,
    className: historyActionPillClasses(label),
    note: ledgerAttachedNote(txn),
    cashFlowId: cashFlowId > 0 ? cashFlowId : null,
    txnId: txnId > 0 ? txnId : null,
  });
}

/** Own-ledger row metadata for broker / account columns. */
export interface HistoryLedgerMetaTxn {
  account_id?: string | null;
  account_label?: string | null;
  account_short_name?: string | null;
  broker_id?: string | null;
  broker_name?: string | null;
  tags?: string[];
  extensions?: Record<string, string>;
  source?: string | null;
  idempotency_key?: string | null;
}

export interface HistoryRowOrigin {
  label: string;
  tip: string;
  /** `inferred` rows rest on the weakest evidence and are called out louder. */
  tone: "derived" | "inferred";
}

/**
 * Rows the importer built rather than read off a broker trade. Robinhood reports
 * no trade when a contract is assigned, so the settlement rows have to come from
 * tax lots or from the option that was open — worth showing, since they are the
 * rows most likely to be wrong.
 */
export function historyRowOrigin(txn: HistoryLedgerMetaTxn): HistoryRowOrigin | null {
  const kind = /^import:(?:rh|tt):([a-z0-9-]+)/i
    .exec(String(txn.idempotency_key ?? ""))?.[1]
    ?.toLowerCase();
  if (!kind) return null;

  if (kind === "synth-put-asgn" || kind === "pnl-put-assignbuy") {
    return {
      label: "inferred",
      tone: "inferred",
      tip: "Built from an option that was open, not from a broker settlement record. Quantity and price are inferred, so check this row against your broker before trusting it.",
    };
  }
  if (kind === "synth-exp") {
    return {
      label: "derived",
      tone: "derived",
      tip: "Expiration recorded because the contract reached its expiry with nothing settling it. Brokers report no trade for an expiration, so the closing row is derived from the open position.",
    };
  }
  if (kind.startsWith("synth-")) {
    return {
      label: "derived",
      tone: "derived",
      tip: "Assignment recorded from broker tax lots. Robinhood reports no trade for an assignment, so the closing row is reconstructed from the lots that settled.",
    };
  }
  return null;
}

function isUndefinedToken(raw: string): boolean {
  const key = raw.trim().toLowerCase();
  return key === "undefined" || key === "undefined:undefined";
}

function titleCaseBroker(raw: string): string {
  const key = raw.trim().toLowerCase();
  if (!key || isUndefinedToken(key)) return "";
  if (key === "robinhood") return "Robinhood";
  if (key === "tastytrade") return "TastyTrades";
  if (key === "manual") return "Manual";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** Trading system / broker for History(L) + Analytics(L) (Robinhood, TastyTrades, Manual, …). */
export function historySystemLabel(txn: HistoryLedgerMetaTxn): string {
  const fromTable = String(txn.broker_name ?? "").trim();
  if (fromTable && !isUndefinedToken(fromTable)) return fromTable;

  const ext = txn.extensions ?? {};
  const fromExt = titleCaseBroker(String(ext.broker ?? ""));
  if (fromExt) return fromExt;

  const brokerId = String(txn.broker_id ?? "").trim();
  const acctId = String(txn.account_id ?? "").toLowerCase();
  if (isUndefinedToken(fromTable) || isUndefinedToken(brokerId) || acctId === "undefined:undefined") {
    return "undefined";
  }

  const tags = txn.tags ?? [];
  if (tags.includes("Robinhood")) return "Robinhood";
  if (tags.includes("TastyTrades")) return "TastyTrades";

  if (acctId.startsWith("robinhood:")) return "Robinhood";
  if (acctId.startsWith("tastytrade:")) return "TastyTrades";
  if (acctId.startsWith("manual:")) return "Manual";

  const src = String(txn.source ?? "").toLowerCase();
  if (src.includes("robinhood")) return "Robinhood";
  if (src.includes("tastytrade")) return "TastyTrades";
  return "undefined";
}

/** @deprecated Use historySystemLabel — kept for older call sites. */
export function historyBrokerLabel(txn: HistoryLedgerMetaTxn): string {
  return historySystemLabel(txn);
}

/** Last four digits of a broker account number (fallback account label). */
export function historyAccountLast4(raw: string | null | undefined): string {
  const digits = String(raw ?? "").replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : "";
}

/** Account label / number for History(L) rows. */
export function historyAccountLabel(
  txn: HistoryLedgerMetaTxn & { account_name?: string | null },
): string {
  const ledgerLabel = String(txn.account_label ?? "").trim();
  if (ledgerLabel && !isUndefinedToken(ledgerLabel)) return ledgerLabel;

  const name = String(txn.account_name ?? "").trim();
  if (name && !isUndefinedToken(name)) return name;

  const ext = txn.extensions ?? {};
  const rhName = String(ext.rh_account_name ?? "").trim();
  if (rhName) return rhName;

  const last4 = historyAccountLast4(ext.rh_account_number ?? ext.broker_account);
  if (last4) return last4;

  const label = String(txn.account_label ?? "").trim();
  const system = historySystemLabel(txn);
  if (label && system && label.toLowerCase() === system.toLowerCase()) return "undefined";

  return label && !isUndefinedToken(label) ? label : "undefined";
}
